// Meta (Facebook) Pixel.
//
// The base code in public/index.html installs the `fbq` stub and starts
// loading fbevents.js. That stub queues every call made before the script
// lands, so the helpers here are safe to call the moment the bundle boots.
//
// PRIVACY — this pixel is deliberately limited to parent-facing pages. No
// child's activity, answer, score or Learning Snapshot is ever reported, and
// no child identifier is ever sent as an event parameter. PARENT_FACING_PATHS
// below is an allowlist rather than a blocklist, so any route added to the app
// later stays untracked until someone opts it in on purpose.

/** Website data source "The Dear Parent Project" in Meta Events Manager. */
const PRODUCTION_PIXEL_ID = '2047364342636545';

// A pixel ID is public by design — it is readable in the page source of every
// site that runs one — so it lives in code the same way the production API URL
// does in services/api.ts. That removes a deploy-time footgun: .env is
// dockerignored, so depending on a build flag meant one forgotten argument
// would silently ship a site that tracked nothing at all.
const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || PRODUCTION_PIXEL_ID;

// Development and `npm test` stay off the ad account. Set
// REACT_APP_META_PIXEL_ID locally to opt a dev build in for testing.
const ENABLED =
  process.env.NODE_ENV === 'production' || Boolean(process.env.REACT_APP_META_PIXEL_ID);

type Fbq = (...args: any[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
  }
}

// Mirrors the <Route> list in App.tsx. /snapshot-preview is deliberately
// absent: it renders a Learning Snapshot, and it only exists in development
// builds anyway.
//
// The games, Learning Journey and results screens all render inside /app
// without changing the URL, so they never produce a page view of their own —
// the single /app page view belongs to the parent arriving at their dashboard.
const PARENT_FACING_PATHS = new Set([
  '/',
  '/about',
  '/login',
  '/app',
  '/app/payment',
  '/app/payment-history',
  '/payment/success',
  '/payment/cancelled',
]);

export const isParentFacingPath = (pathname: string): boolean =>
  PARENT_FACING_PATHS.has(pathname.replace(/(.)\/+$/, '$1'));

// Standard events Meta recognises for optimisation and reporting. Anything
// outside this list has to go through trackCustom, where Meta treats it as an
// opaque custom conversion.
export type StandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'Contact'
  | 'Subscribe';

export interface EventOptions {
  /**
   * Stable identifier for one real-world action. Meta drops repeats of the
   * same eventID within ~48h, which protects against a user refreshing a
   * confirmation page and against double-counting if the Conversions API is
   * ever added server-side.
   */
  eventID?: string;
}

let initialized = false;

const ready = (): boolean =>
  ENABLED && typeof window !== 'undefined' && typeof window.fbq === 'function';

/** Binds the pixel ID to the loaded script. Safe to call more than once. */
export function initMetaPixel(): void {
  if (initialized || !ready()) return;
  window.fbq!('init', PIXEL_ID);
  initialized = true;
}

export function trackEvent(
  event: StandardEvent,
  params?: Record<string, unknown>,
  options?: EventOptions
): void {
  if (!ready()) return;
  initMetaPixel();
  if (options) window.fbq!('track', event, params ?? {}, options);
  else window.fbq!('track', event, params ?? {});
}

export function trackCustom(
  event: string,
  params?: Record<string, unknown>,
  options?: EventOptions
): void {
  if (!ready()) return;
  initMetaPixel();
  if (options) window.fbq!('trackCustom', event, params ?? {}, options);
  else window.fbq!('trackCustom', event, params ?? {});
}

/** No-ops on any page that is not parent-facing. */
export function trackPageView(pathname: string): void {
  if (!isParentFacingPath(pathname)) return;
  trackEvent('PageView');
}
